import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, ArrowLeft, Search, AlertCircle, BookOpen } from 'lucide-react';
import { Button } from '@heroui/react';
import { useTranslation } from '@/hooks/useTranslation';

const NotFoundPage: React.FC = () => {
  const { t } = useTranslation('common');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      {/* Decorative background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-96 h-96 bg-gradient-to-br from-blue-400/10 to-purple-600/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 -right-4 w-96 h-96 bg-gradient-to-br from-pink-400/10 to-indigo-600/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-2xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* 404 Number */}
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative inline-block">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-3xl blur-2xl opacity-50" />
              <div className="relative">
                <h1 className="text-9xl sm:text-[12rem] font-extrabold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  404
                </h1>
              </div>
            </div>
          </motion.div>

          {/* Icon */}
          <motion.div
            className="mb-6 flex justify-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl blur-lg opacity-50" />
              <div className="relative p-4 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-lg">
                <AlertCircle className="w-12 h-12 text-white" />
              </div>
            </div>
          </motion.div>

          {/* Title */}
          <motion.h2
            className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            {t('notFound.title', 'Page Not Found')}
          </motion.h2>

          {/* Description */}
          <motion.p
            className="text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {t(
              'notFound.description',
              "Sorry, we couldn't find the page you're looking for. It might have been moved, deleted, or you don't have permission to access it.",
            )}
          </motion.p>

          {/* Action Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <Button
              as={Link}
              to="/"
              color="primary"
              size="lg"
              startContent={<Home className="w-5 h-5" />}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold shadow-lg hover:shadow-xl transition-shadow"
            >
              {t('notFound.goHome', 'Go to Home')}
            </Button>
            <Button
              as={Link}
              to="/products"
              variant="bordered"
              size="lg"
              startContent={<Search className="w-5 h-5" />}
              className="font-semibold"
            >
              {t('notFound.browseProducts', 'Browse Products')}
            </Button>
            <Button
              as={Link}
              to="/guides"
              variant="bordered"
              size="lg"
              startContent={<BookOpen className="w-5 h-5" />}
              className="font-semibold"
            >
              {t('notFound.browseGuides', 'Browse Guides')}
            </Button>
            <Button
              variant="light"
              size="lg"
              startContent={<ArrowLeft className="w-5 h-5" />}
              onClick={() => window.history.back()}
              className="font-semibold"
            >
              {t('notFound.goBack', 'Go Back')}
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default NotFoundPage;

